import booleanContains from "@turf/boolean-contains";
import { AnySupportedGeometry } from "@iventis/map-types";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { SitemapVersion } from "@iventis/domain-model/model/sitemapVersion";

/** Checks if a given map object is in the perimeter of a sitemap and returns the sitemap's id */
export function getMapObjectSitemap(mapObjectGeometry: AnySupportedGeometry, mapObjectLevel: number, sitemaps: Sitemap[], _sitemapConfig: MapSitemapConfig[]): string | null {
    // Early exit if any of the required parameters are missing
    if (mapObjectLevel == null || mapObjectGeometry == null || sitemaps == null || sitemaps.length === 0) {
        return null;
    }

    // Ensure sitemapConfig is not null
    const sitemapConfig = _sitemapConfig || [];

    // Reduce to a single sitemap that the map object belongs to
    const sitemapMapObjectBelongsTo = sitemaps.reduce<Sitemap | null>((currentSitemap, sitemap) => {
        // Get most recent sitemap version
        const mostRecentSitemapVersion = sitemap.versions.reduce<SitemapVersion | undefined>((mostRecentSitemap, sitemap) => {
            // If not set, return current sitemap
            if (mostRecentSitemap == null) {
                return sitemap;
            }
            // Return any newer sitemaps than the current
            return new Date(mostRecentSitemap.date).getTime() > new Date(sitemap.date).getTime() ? mostRecentSitemap : sitemap;
        }, undefined);

        // Get the level from the sitemap version
        const sitemapVersionLevel = mostRecentSitemapVersion?.sitemapVersionLevels.find((sitemapVersionLevel) => sitemapVersionLevel.levelIndex === mapObjectLevel);
        // If no level found, return existing current sitemap
        if (sitemapVersionLevel == null) {
            return currentSitemap;
        }

        // Check it is within the perimeter of the level
        const withinPerimeter = sitemapVersionLevel != null && sitemapVersionLevel.perimeter != null && booleanContains(sitemapVersionLevel.perimeter, mapObjectGeometry);
        if (withinPerimeter) {
            // If it is, return if we have no chosen sitemap yet
            if (currentSitemap == null) {
                return sitemap;
            }

            // If currentSitemap is already set, we need to decide which one to return
            const isSitemapWeAreCheckingInConfig = sitemapConfig.some((sitemapInView) => sitemapInView.sitemapId === sitemap.id);
            const isCurrentlySelectedSitemapInConfig = sitemapConfig.some((sitemapInView) => sitemapInView.sitemapId === currentSitemap?.id);

            // First if the found sitemap in the config, and the existing currentSitemap is not, return the one in the config
            if (isSitemapWeAreCheckingInConfig && !isCurrentlySelectedSitemapInConfig) {
                return sitemap;
            }
            // Otherwise, as long as they match (both in config or both not), return the one that contains the map object within its bounding
            if (isSitemapWeAreCheckingInConfig === isCurrentlySelectedSitemapInConfig) {
                const withinBounds = booleanContains(sitemapVersionLevel.bounding, mapObjectGeometry);
                if (withinBounds) {
                    return sitemap;
                }
                return currentSitemap;
            }
        }

        // If all checks fail return the existing chosen sitemap
        return currentSitemap;
    }, null);

    return sitemapMapObjectBelongsTo?.id ?? null;
}
